/**
 * Enrollment form — three sectioned cards, single Formik form.
 *
 * Uses the shared ImageUploader (which has its own redux dependency for
 * toast feedback). When this form moves to the future public/anonymous
 * page, ImageUploader may need a small refactor to accept a callback for
 * error display, but the surface area is small.
 */

import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useEffect,
  useState
} from "react"
import {
  Formik,
  Form,
  FormikProps,
  FormikErrors,
  useFormikContext
} from "formik"
import { object, string } from "yup"
import {
  Building2,
  Palette,
  UserRound,
  Lock,
  Settings2,
  ChevronDown
} from "lucide-react"
import Input from "../../../common/ui/form/input"
import Select from "../../../common/ui/form/select"
import { ImageUploader } from "../../../common/ui"
import {
  PRICING_ENGINE_LABELS,
  PricingEngineType
} from "../../../types/pricing-engine"
import { BrandSettings, EnrollApiMode, EnrollRequest } from "../types"

export interface EnrollFormValues {
  company_name: string
  org_name: string
  company_nmls: string
  website: string
  api_mode: EnrollApiMode | ""
  primary_color: string
  secondary_color: string
  logo_url: string
  brand_settings: {
    disclaimer: string
    msa_template: string
    static_disclaimer: string
    about: string
    payment_details_disclaimer: string
    lead_capture_disclaimer: string
    loan_calc_disclaimer: string
    subscription_email_disclaimer: string
    third_party_fees_disclaimer: string
    ob_channel_id: string
    zillow_brand_name: string
    google_place_id: string
    experience_id: string
    yelp_id: string
    privacy_policy_url: string
    terms_and_conditions_url: string
    non_bank: boolean
    disable_subscription_emails: boolean
    disable_itemized_fees: boolean
    disable_receipt_emails: boolean
  }
  first_user: {
    first_name: string
    last_name: string
    email: string
    nmls: string
    phone: string
    password: string
  }
}

export const initialEnrollValues: EnrollFormValues = {
  company_name: "",
  org_name: "",
  company_nmls: "",
  website: "",
  api_mode: "",
  primary_color: "",
  secondary_color: "",
  logo_url: "",
  brand_settings: {
    disclaimer: "",
    msa_template: "",
    static_disclaimer: "",
    about: "",
    payment_details_disclaimer: "",
    lead_capture_disclaimer: "",
    loan_calc_disclaimer: "",
    subscription_email_disclaimer: "",
    third_party_fees_disclaimer: "",
    ob_channel_id: "",
    zillow_brand_name: "",
    google_place_id: "",
    experience_id: "",
    yelp_id: "",
    privacy_policy_url: "",
    terms_and_conditions_url: "",
    non_bank: false,
    disable_subscription_emails: false,
    disable_itemized_fees: false,
    disable_receipt_emails: false
  },
  first_user: {
    first_name: "",
    last_name: "",
    email: "",
    nmls: "",
    phone: "",
    password: ""
  }
}

const HEX_RX = /^#[0-9a-fA-F]{6}$/
const DIGITS_RX = /^\d+$/
const PHONE_RX = /^[\d\s()+\-]{7,20}$/

const validationSchema = object({
  company_name: string()
    .trim()
    .required("Company name is required")
    .max(255, "Max 255 characters"),
  company_nmls: string()
    .trim()
    .required("Company NMLS is required")
    .matches(DIGITS_RX, "Digits only")
    .max(20, "Max 20 digits"),
  website: string()
    .trim()
    .required("Website is required")
    .url("Must be a valid URL (https://…)"),
  org_name: string().trim().max(255, "Max 255 characters"),
  api_mode: string(),
  primary_color: string().matches(HEX_RX, {
    excludeEmptyString: true,
    message: "Must be #RRGGBB"
  }),
  secondary_color: string().matches(HEX_RX, {
    excludeEmptyString: true,
    message: "Must be #RRGGBB"
  }),
  logo_url: string().url({
    excludeEmptyString: true
  } as any),
  first_user: object({
    first_name: string().trim().required("First name is required"),
    last_name: string().trim().required("Last name is required"),
    email: string()
      .trim()
      .required("Email is required")
      .email("Invalid email address"),
    nmls: string()
      .trim()
      .required("LO NMLS is required")
      .matches(DIGITS_RX, "Digits only")
      .max(20, "Max 20 digits"),
    phone: string().matches(PHONE_RX, {
      excludeEmptyString: true,
      message: "Use digits, spaces, ()+- (7–20 chars)"
    }),
    password: string()
  })
})

interface FormSectionProps {
  index: string
  title: string
  description?: string
  icon: React.ReactNode
  children: React.ReactNode
}

const FormSection = ({
  index,
  title,
  description,
  icon,
  children
}: FormSectionProps) => (
  <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
    <header className="flex items-start gap-4 px-6 pt-6 pb-4 border-b border-neutral-100">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
            {index}
          </span>
          <h2 className="font-serif text-lg text-neutral-900">{title}</h2>
        </div>
        {description && (
          <p className="mt-1 text-sm text-neutral-500">{description}</p>
        )}
      </div>
    </header>
    <div className="px-6 py-6 space-y-5">{children}</div>
  </section>
)

const ColorField = ({
  name,
  label,
  placeholder
}: {
  name: string
  label: string
  placeholder: string
}) => {
  const { values, setFieldValue } = useFormikContext<EnrollFormValues>()
  const value = (values as any)[name] as string
  const isValidHex = HEX_RX.test(value)

  return (
    <div>
      <Input label={label} name={name} placeholder={placeholder} />
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          aria-label={`Choose ${label.toLowerCase()}`}
          className="relative h-9 w-9 rounded-md border border-neutral-200 overflow-hidden cursor-pointer"
          style={{ backgroundColor: isValidHex ? value : "transparent" }}
        >
          {!isValidHex && (
            <span className="absolute inset-0 flex items-center justify-center text-neutral-300">
              ?
            </span>
          )}
          <input
            type="color"
            value={isValidHex ? value : "#013a70"}
            onChange={e => setFieldValue(name, e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </button>
        <span className="text-xs text-neutral-400 font-mono">
          {isValidHex ? value.toUpperCase() : "—"}
        </span>
      </div>
    </div>
  )
}

const LogoUpload = () => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<EnrollFormValues>()

  return (
    <div>
      <label className="block font-medium mb-1">
        Logo <span className="text-neutral-400 text-sm">(optional)</span>
      </label>
      <ImageUploader
        isFullRounded={false}
        imageUrl={values.logo_url}
        onChangeImageUrl={url => setFieldValue("logo_url", url)}
      />
      {errors.logo_url && touched.logo_url && (
        <p className="mt-1 text-sm text-red-600">{errors.logo_url}</p>
      )}
    </div>
  )
}

const ApiModeSelect = () => {
  const { values, setFieldValue, touched, errors } =
    useFormikContext<EnrollFormValues>()

  const options = useMemo(
    () =>
      (
        Object.entries(PRICING_ENGINE_LABELS) as [PricingEngineType, string][]
      ).map(([id, name]) => ({ id, name })),
    []
  )

  return (
    <Select
      label="Pricing engine"
      name="api_mode"
      data={options}
      selected={values.api_mode}
      setSelected={val => setFieldValue("api_mode", val)}
      error={
        touched.api_mode && errors.api_mode
          ? (errors.api_mode as string)
          : undefined
      }
    />
  )
}

const Toggle = ({ name, label }: { name: string; label: string }) => {
  const { values, setFieldValue } = useFormikContext<EnrollFormValues>()
  const parts = name.split(".")
  let val: any = values
  for (const p of parts) val = val?.[p]
  const checked = !!val

  return (
    <label className="flex items-center gap-3 cursor-pointer py-1">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => setFieldValue(name, !checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-primary-500" : "bg-neutral-300"
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-[18px]" : "translate-x-[3px]"
          }`}
        />
      </button>
      <span className="text-sm text-neutral-700">{label}</span>
    </label>
  )
}

const BRAND_SETTINGS_STRING_KEYS: {
  key: keyof EnrollFormValues["brand_settings"]
  label: string
}[] = [
  { key: "disclaimer", label: "Disclaimer" },
  { key: "msa_template", label: "MSA template" },
  { key: "static_disclaimer", label: "Static disclaimer" },
  { key: "about", label: "About" },
  { key: "payment_details_disclaimer", label: "Payment details disclaimer" },
  { key: "lead_capture_disclaimer", label: "Lead capture disclaimer" },
  { key: "loan_calc_disclaimer", label: "Loan calculator disclaimer" },
  {
    key: "subscription_email_disclaimer",
    label: "Subscription email disclaimer"
  },
  { key: "third_party_fees_disclaimer", label: "Third-party fees disclaimer" }
]

const BrandSettingsSection = () => {
  const [open, setOpen] = useState(false)
  const { values } = useFormikContext<EnrollFormValues>()

  const bs = values.brand_settings
  const filledCount = Object.entries(bs).filter(([, v]) =>
    typeof v === "boolean" ? v : !!v
  ).length

  return (
    <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-start gap-4 px-6 py-5 text-left"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
          <Settings2 size={20} />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
              04 / Brand settings
            </span>
            <h2 className="font-serif text-lg text-neutral-900">
              Advanced brand config
            </h2>
          </div>
          <p className="mt-1 text-sm text-neutral-500">
            Disclaimers, integration IDs, and feature toggles.
            {filledCount > 0 && (
              <span className="ml-1 font-medium text-primary-500">
                {filledCount} set
              </span>
            )}
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`mt-3 shrink-0 text-neutral-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="border-t border-neutral-100 px-6 py-6 space-y-6">
          {/* Toggles */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3">
              Feature flags
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Toggle name="brand_settings.non_bank" label="Non-bank lender" />
              <Toggle
                name="brand_settings.disable_subscription_emails"
                label="Disable subscription emails"
              />
              <Toggle
                name="brand_settings.disable_itemized_fees"
                label="Disable itemized fees"
              />
              <Toggle
                name="brand_settings.disable_receipt_emails"
                label="Disable receipt emails"
              />
            </div>
          </div>

          {/* URLs */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3">
              Legal URLs
            </div>
            <div className="space-y-4">
              <Input
                label="Privacy policy URL"
                name="brand_settings.privacy_policy_url"
                placeholder="https://…"
              />
              <Input
                label="Terms & conditions URL"
                name="brand_settings.terms_and_conditions_url"
                placeholder="https://…"
              />
            </div>
          </div>

          {/* Integration IDs */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3">
              Integration IDs
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="OB channel ID"
                name="brand_settings.ob_channel_id"
              />
              <Input
                label="Zillow brand name"
                name="brand_settings.zillow_brand_name"
              />
              <Input
                label="Google Place ID"
                name="brand_settings.google_place_id"
              />
              <Input
                label="Experience ID"
                name="brand_settings.experience_id"
              />
              <Input label="Yelp ID" name="brand_settings.yelp_id" />
            </div>
          </div>

          {/* Disclaimers */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3">
              Disclaimers &amp; content
            </div>
            <p className="text-xs text-neutral-400 mb-4">
              HTML is allowed in disclaimer fields.
            </p>
            <div className="space-y-4">
              {BRAND_SETTINGS_STRING_KEYS.map(({ key, label }) => (
                <Input key={key} label={label} name={`brand_settings.${key}`} />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export interface EnrollFormHandle {
  /** Surface a server validation error onto Formik fields. */
  applyServerErrors: (errors: FormikErrors<EnrollFormValues>) => void
}

interface Props {
  /** Whether the form is locked (during submit / polling / terminal states). */
  locked: boolean
  submitLabel: string
  onSubmit: (values: EnrollFormValues) => void
  /** Optional live values callback so the right-panel preview can mirror typing. */
  onValuesChange?: (values: EnrollFormValues) => void
}

const ValuesWatcher = ({
  onChange
}: {
  onChange: (values: EnrollFormValues) => void
}) => {
  const { values } = useFormikContext<EnrollFormValues>()
  // Re-emit on every change. Cheap; the parent only stores into state.
  useEffect(() => {
    onChange(values)
  }, [values, onChange])
  return null
}

const EnrollForm = forwardRef<EnrollFormHandle, Props>(
  ({ locked, submitLabel, onSubmit, onValuesChange }, ref) => {
    const formikRef = useRef<FormikProps<EnrollFormValues> | null>(null)
    const [showPassword, setShowPassword] = useState(false)

    useImperativeHandle(ref, () => ({
      applyServerErrors: errors => {
        formikRef.current?.setErrors(errors)
        formikRef.current?.setSubmitting(false)
      }
    }))

    return (
      <Formik
        initialValues={initialEnrollValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          onSubmit(values)
        }}
        innerRef={instance => {
          formikRef.current = instance
        }}
      >
        {() => (
          <Form className="space-y-6">
            {onValuesChange && <ValuesWatcher onChange={onValuesChange} />}

            {/* SECTION 01 — COMPANY */}
            <FormSection
              index="01 / Company"
              title="Who are you onboarding?"
              description="The basics that identify the lender. NMLS uniqueness is checked on the server."
              icon={<Building2 size={20} />}
            >
              <Input
                label="Company name"
                name="company_name"
                placeholder="FiveStar Mortgage"
                required
              />
              <Input
                label="Org name"
                name="org_name"
                placeholder="Same as company name if blank"
              />
              <Input
                label="Company NMLS"
                name="company_nmls"
                placeholder="123456"
                required
              />
              <Input
                label="Website"
                name="website"
                placeholder="https://fivestar.mortgage"
                required
              />
              <div>
                <ApiModeSelect />
                <p className="mt-1 text-xs text-neutral-400">
                  Leave blank to skip rateflow and pricing profile setup.
                </p>
              </div>
            </FormSection>

            {/* SECTION 02 — BRANDING */}
            <FormSection
              index="02 / Branding"
              title="Make it look like them"
              description="All optional. Defaults are reasonable; admins can fine-tune later."
              icon={<Palette size={20} />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <ColorField
                  name="primary_color"
                  label="Primary color"
                  placeholder="#013a70"
                />
                <ColorField
                  name="secondary_color"
                  label="Secondary color"
                  placeholder="#013a70"
                />
              </div>
              <LogoUpload />
            </FormSection>

            {/* SECTION 03 — BRAND SETTINGS */}
            <BrandSettingsSection />

            {/* SECTION 04 — FIRST USER */}
            <FormSection
              index="04 / First admin user"
              title="The first loan officer"
              description="This account becomes the brand admin. Their NMLS must be unique across all LOs."
              icon={<UserRound size={20} />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input
                  label="First name"
                  name="first_user.first_name"
                  required
                />
                <Input label="Last name" name="first_user.last_name" required />
              </div>
              <Input
                label="Email"
                name="first_user.email"
                type="email"
                placeholder="jimmy@fivestar.mortgage"
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input
                  label="LO NMLS"
                  name="first_user.nmls"
                  placeholder="654321"
                  required
                />
                <Input
                  label="Phone"
                  name="first_user.phone"
                  placeholder="(555) 555-0100"
                />
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex gap-3">
                  <Lock size={16} className="mt-0.5 shrink-0 text-amber-600" />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <strong>Heads up.</strong> If you skip the password, the
                    user will receive a random one and will need to reset it
                    before logging in. Passwords entered here are{" "}
                    <strong>never replayable</strong> — if a step later in the
                    enrollment fails and you retry, the password is gone.
                  </div>
                </div>
              </div>

              <div className="relative">
                <Input
                  label="Password (optional)"
                  name="first_user.password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Leave blank to send a reset link"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-9 text-xs text-primary-500 hover:text-primary-600"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </FormSection>

            <div className="flex items-center justify-end pt-2">
              <button
                type="submit"
                disabled={locked}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                {submitLabel}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    )
  }
)

EnrollForm.displayName = "EnrollForm"

/**
 * Convert a Formik values object into the EnrollRequest payload sent to the
 * server. Strips empty optional fields so we don't send `""` values that the
 * server has to special-case.
 */
export const buildEnrollPayload = (
  values: EnrollFormValues
): Omit<EnrollRequest, "idempotency_key"> => {
  const payload: Omit<EnrollRequest, "idempotency_key"> = {
    company_name: values.company_name.trim(),
    company_nmls: values.company_nmls.trim(),
    website: values.website.trim(),
    first_user: {
      first_name: values.first_user.first_name.trim(),
      last_name: values.first_user.last_name.trim(),
      email: values.first_user.email.trim(),
      nmls: values.first_user.nmls.trim()
    }
  }
  if (values.org_name.trim()) payload.org_name = values.org_name.trim()
  if (values.api_mode) payload.api_mode = values.api_mode as EnrollApiMode
  if (values.primary_color) payload.primary_color = values.primary_color
  if (values.secondary_color) payload.secondary_color = values.secondary_color
  if (values.logo_url) payload.logo_url = values.logo_url.trim()

  // Only send brand_settings with non-empty values
  const bs: BrandSettings = {}
  const { brand_settings } = values
  for (const [k, v] of Object.entries(brand_settings)) {
    if (typeof v === "boolean") {
      if (v) (bs as any)[k] = v
    } else if (typeof v === "string" && v.trim()) {
      ;(bs as any)[k] = v.trim()
    }
  }
  if (Object.keys(bs).length > 0) payload.brand_settings = bs

  if (values.first_user.phone)
    payload.first_user.phone = values.first_user.phone.trim()
  if (values.first_user.password)
    payload.first_user.password = values.first_user.password
  return payload
}

export default EnrollForm
